import React, { useState, useEffect, useRef } from "react";
import { Button, Divider, Form, Message, Modal } from "semantic-ui-react";
import PropTypes from "prop-types";
import { ConfirmModalAction } from "./confirm-modal";
import { StyledTriggerLink } from "./trigger-link";
import { t } from "../../constant/translations";

export const EditEntity = ({
  entity,
  error,
  initialData,
  loading,
  create,
  update,
  del,
  clearError,
  manager,
  useTextTrigger,
  trigger_props,
  deleteProps,
  customTrigger,
  triggerElement,
  forceOpen,
  onForceClose,
  header,
  children,
  lastModified,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const prevLoadingRef = useRef(loading);
  const prevLastModifiedRef = useRef(lastModified);

  useEffect(() => {
    const prevLoading = prevLoadingRef.current;
    const prevLastModified = prevLastModifiedRef.current;

    // Close when loading finishes successfully
    if (prevLoading && !loading && !error.exists()) {
      setModalOpen(false);
    }
    // Close when lastModified changes (new entity saved)
    if (prevLastModified == null && lastModified && !error.exists()) {
      setModalOpen(false);
    }

    prevLoadingRef.current = loading;
    prevLastModifiedRef.current = lastModified;
  }, [loading, lastModified, error]);

  useEffect(() => {
    if (forceOpen) setModalOpen(true);
  }, [forceOpen]);

  const handleOpenModal = () => setModalOpen(true);

  const handleCloseModal = () => {
    setModalOpen(false);
    if (onForceClose) onForceClose();
    if (clearError) {
      clearError();
    }
    if (manager) {
      manager.clear();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (entity) {
      update(manager.formData);
    } else {
      create(manager.formData);
    }
  };

  let trigger = (
    <Button
      size={"tiny"}
      onClick={handleOpenModal}
      basic={true}
      icon={entity ? "edit" : "plus"}
      {...(trigger_props || {})}
    />
  );

  if (customTrigger) {
    trigger = (
      <StyledTriggerLink href={"#"} onClick={handleOpenModal}>
        {`${customTrigger.text}`}
      </StyledTriggerLink>
    );
  }

  if (useTextTrigger) {
    trigger = (
      <StyledTriggerLink href={"#"} onClick={handleOpenModal}>
        {entity ? t("labels.change") : t("labels.create")}
      </StyledTriggerLink>
    );
  }

  if (triggerElement) {
    trigger = React.cloneElement(triggerElement, { onClick: handleOpenModal });
  }

  return (
    <Modal
      open={modalOpen}
      onOpen={handleOpenModal}
      onClose={handleCloseModal}
      closeIcon={"close"}
      trigger={trigger}
    >
      <Modal.Header>{header}</Modal.Header>
      <Modal.Content>
        <Form as="div">
          {modalOpen && children}
          <Divider horizontal />
          {error.exists() && (
            <Message error visible={true}>
              {error.getGlobal()}
            </Message>
          )}
          <Form.Group>
            {del && entity && (
              <ConfirmModalAction
                header={t("labels.deletion")}
                content={t("labels.confirm_continue")}
                trigger_props={{
                  negative: true,
                  fluid: true,
                  size: "medium",
                  content:
                    deleteProps && deleteProps.triggerText
                      ? deleteProps.triggerText
                      : t("labels.delete"),
                  basic: false,
                }}
                {...deleteProps}
                loading={loading}
                error={error}
                action={() => del(entity)}
              />
            )}
            <Button
              type="submit"
              positive
              fluid
              size="medium"
              loading={loading}
              onClick={handleSubmit}
            >
              {t("labels.save")}
            </Button>
          </Form.Group>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

EditEntity.propTypes = {
  entity: PropTypes.object,
  error: PropTypes.object.isRequired,
  initialData: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  create: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  del: PropTypes.func,
  clearError: PropTypes.func,
  manager: PropTypes.object,
  useTextTrigger: PropTypes.bool,
  trigger_props: PropTypes.object,
  deleteProps: PropTypes.object,
  customTrigger: PropTypes.object,
  triggerElement: PropTypes.element,
  header: PropTypes.string,
  children: PropTypes.node,
  lastModified: PropTypes.any,
};
